import { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Tag, Search, X } from "lucide-react";
import { PARTNER_DETTAGLI } from "@/data/partnerConvenzioniData";

const CATEGORIE = [
  "Auto, Mobilità e Trasporti",
  "Banche e Assicurazioni",
  "Energy e Utilities",
  "Famiglia, Cultura e Tempo Libero",
  "Prodotti e Fornitura per l'impresa",
  "Servizi per l'Impresa (comunicazione, marketing, IT)",
  "Welfare e Soluzioni per i dipendenti",
];

const CATEGORIE_COLORS = {
  "Auto, Mobilità e Trasporti":                           { chip: "bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200",   active: "bg-slate-700 text-white border-slate-700" },
  "Banche e Assicurazioni":                               { chip: "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100",         active: "bg-teal-600 text-white border-teal-600" },
  "Energy e Utilities":                                   { chip: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100",     active: "bg-amber-500 text-white border-amber-500" },
  "Famiglia, Cultura e Tempo Libero":                     { chip: "bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100",         active: "bg-pink-500 text-white border-pink-500" },
  "Prodotti e Fornitura per l'impresa":                   { chip: "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100", active: "bg-orange-500 text-white border-orange-500" },
  "Servizi per l'Impresa (comunicazione, marketing, IT)": { chip: "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100", active: "bg-indigo-600 text-white border-indigo-600" },
  "Welfare e Soluzioni per i dipendenti":                 { chip: "bg-green-50 text-green-700 border-green-200 hover:bg-green-100",     active: "bg-green-600 text-white border-green-600" },
};

const TUTTI = "Tutte";
const ALL_PARTNERS = Object.entries(PARTNER_DETTAGLI).map(([slug, p]) => ({ slug, ...p }));

const MACRO_FILTERS = [
  { id: "tutte",           label: "Tutte" },
  { id: "primo-piano",     label: "In primo piano" },
  { id: "territoriali",    label: "Convenzioni territoriali" },
  { id: "nazionali",       label: "Convenzioni nazionali" },
];

export default function ConvenzioniPage() {
  const [activeTab, setActiveTab] = useState(TUTTI);
  const [macro, setMacro] = useState("tutte");
  const [q, setQ] = useState("");
  const filterRef = useRef(null);
  const nazionaliRef = useRef(null);
  const scrollToResults = () => {
    if (filterRef.current) {
      window.scrollTo({ top: filterRef.current.offsetTop - (window.innerWidth >= 1024 ? 168 : 104), behavior: "smooth" });
    }
  };
  const scrollToNazionali = () => {
    if (nazionaliRef.current) {
      window.scrollTo({ top: nazionaliRef.current.offsetTop - (window.innerWidth >= 1024 ? 168 : 104), behavior: "smooth" });
    }
  };

  const handleMacroClick = (id) => {
    if (id === "nazionali") {
      // Non filtra la griglia: porta l'utente alla sezione dedicata alle convenzioni nazionali
      setMacro("nazionali");
      scrollToNazionali();
      return;
    }
    // Doppio filtraggio: NON resetta la categoria selezionata
    setMacro(id);
    scrollToResults();
  };

  const normalize = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  // Applica solo il filtro macro (senza categoria/query) — base per calcolare i counter delle categorie
  const applyMacro = (list, m) => {
    if (m === "primo-piano") return list.filter((p) => p.inPrimoPiano === true);
    if (m === "nazionali") return [];
    return list;
  };
  // Applica solo il filtro categoria — base per calcolare i counter dei macro
  const applyCategoria = (list, cat) => (cat === TUTTI ? list : list.filter((p) => p.categoria === cat));

  const filtered = useMemo(() => {
    let list = applyMacro(ALL_PARTNERS, macro);
    list = applyCategoria(list, activeTab);
    // Filtro ricerca testuale
    const qq = normalize(q).trim();
    if (qq) {
      const tokens = qq.split(/\s+/).filter(Boolean);
      list = list.filter((p) => {
        const hay = normalize(`${p.nome} ${p.tagline || ""} ${p.descrizione || ""} ${p.categoria || ""} ${p.scontoLabel || ""}`);
        return tokens.every((t) => hay.includes(t));
      });
    }
    // Ordinamento alfabetico per nome del partner (case/accent-insensitive)
    return [...list].sort((a, b) => normalize(a.nome).localeCompare(normalize(b.nome), "it"));
  }, [macro, activeTab, q]);

  // Contatori dei chip macro — sempre statici sul totale complessivo (non cross-filtrati dal settore attivo)
  const macroCount = useMemo(() => {
    return {
      "tutte":         ALL_PARTNERS.length,
      "primo-piano":   ALL_PARTNERS.filter((p) => p.inPrimoPiano === true).length,
      "territoriali":  ALL_PARTNERS.length,
      "nazionali":     null, // sezione dedicata (link)
    };
  }, []);

  // Contatori delle categorie — sempre statici sul totale complessivo (non cross-filtrati dalla categoria macro attiva)
  const categoriaCount = useMemo(() => {
    const map = {};
    CATEGORIE.forEach((cat) => { map[cat] = ALL_PARTNERS.filter((p) => p.categoria === cat).length; });
    return map;
  }, []);

  return (
    <main id="main-content" data-testid="convenzioni-page">

      {/* ── HERO ── */}
      <section className="relative overflow-hidden" aria-labelledby="convenzioni-title">
        <img
          src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&q=60&auto=format"
          alt="Convenzioni"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 relative">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
            Vantaggi per gli associati
          </span>
          <h1 id="convenzioni-title" className="text-4xl md:text-5xl font-bold text-white mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            Convenzioni
          </h1>
          <p className="text-lg text-white/80 mt-4 max-w-3xl">
            Gli Associati di Confcommercio Roma possono godere di sconti e servizi a condizioni agevolate, a supporto dell'attività imprenditoriale e delle esigenze personali.
          </p>

          {/* Search — stile identico alla pagina Associazioni */}
          <div className="mt-6 max-w-xl" data-testid="convenzioni-search-wrap">
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" aria-hidden="true" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca una convenzione..."
                data-testid="convenzioni-search"
                className="w-full pl-11 pr-10 py-3.5 rounded-xl bg-white/15 border border-white/25 text-white placeholder-white/50 focus:outline-none focus:border-white/60 focus:bg-white/20 transition-all text-sm"
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                  aria-label="Cancella ricerca"
                  data-testid="convenzioni-search-clear"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── FILTRI + GRIGLIA ── */}
      <section className="pt-0 pb-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Barra filtri sticky — due gruppi distinti, come in /associazioni */}
          <div ref={filterRef} className="sticky top-[104px] lg:top-[168px] z-20 bg-white shadow-sm -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 mb-10">

            {/* GRUPPO 1 — Categorie (Tutte / In primo piano / Territoriali / Nazionali), stile tab */}
            <div className="flex overflow-x-auto border-b border-[var(--confcom-border)]" style={{ scrollbarWidth: "none" }} data-testid="convenzioni-filters-categorie">
              <button
                onClick={() => { setMacro("tutte"); scrollToResults(); }}
                data-testid="macro-tutte"
                className={`shrink-0 flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-all whitespace-nowrap ${
                  macro === "tutte"
                    ? "border-[var(--confcom-blue)] text-[var(--confcom-blue)]"
                    : "border-transparent text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-text-primary)] hover:border-gray-300"
                }`}
              >
                Tutte
                <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${macro === "tutte" ? "bg-[var(--confcom-blue)] text-white" : "bg-gray-100 text-gray-500"}`}>
                  {macroCount["tutte"]}
                </span>
              </button>

              {MACRO_FILTERS.filter((mf) => mf.id !== "tutte").map((mf) => {
                const isActive = macro === mf.id;
                const count = macroCount[mf.id];
                return (
                  <button
                    key={mf.id}
                    onClick={() => handleMacroClick(mf.id)}
                    data-testid={`macro-${mf.id}`}
                    className={`shrink-0 flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-all whitespace-nowrap ${
                      isActive
                        ? "border-[var(--confcom-blue)] text-[var(--confcom-blue)]"
                        : "border-transparent text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-text-primary)] hover:border-gray-300"
                    }`}
                  >
                    {mf.label}
                    {count !== null && (
                      <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? "bg-[var(--confcom-blue)] text-white" : "bg-gray-100 text-gray-500"}`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* GRUPPO 2 — Settori (categorie merceologiche esistenti), stile chip */}
            <div className="flex flex-wrap items-center gap-2 py-3" data-testid="convenzioni-filters-settori">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--confcom-text-muted)] shrink-0">Settore</span>

              <button
                onClick={() => { setActiveTab(TUTTI); scrollToResults(); }}
                data-testid="settore-tutte"
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-all ${
                  activeTab === TUTTI
                    ? "bg-[var(--confcom-blue)] text-white border-[var(--confcom-blue)]"
                    : "border-[var(--confcom-border)] text-[var(--confcom-text-secondary)] hover:border-[var(--confcom-blue)] hover:text-[var(--confcom-blue)]"
                }`}
              >
                Tutte
                <span className={`text-[10px] font-bold px-1 py-0.5 rounded-full leading-none ${activeTab === TUTTI ? "bg-white/25 text-white" : "bg-white/60"}`}>
                  {macroCount["tutte"]}
                </span>
              </button>

              {CATEGORIE.map((cat) => {
                const isActive = activeTab === cat;
                const cc = CATEGORIE_COLORS[cat] || { chip: "border-[var(--confcom-border)] text-[var(--confcom-text-secondary)]", active: "bg-[var(--confcom-blue)] text-white border-[var(--confcom-blue)]" };
                const count = categoriaCount[cat] ?? 0;
                return (
                  <button
                    key={cat}
                    data-testid={`settore-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 24)}`}
                    onClick={() => { setActiveTab(isActive ? TUTTI : cat); scrollToResults(); }}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-all ${isActive ? cc.active : cc.chip}`}
                  >
                    {cat}
                    <span className={`text-[10px] font-bold px-1 py-0.5 rounded-full leading-none ${isActive ? "bg-white/25 text-white" : "bg-white/60"}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Griglia partner */}
          {filtered.length === 0 ? (
            <p className="text-[var(--confcom-text-muted)] text-center py-16" data-testid="convenzioni-empty">
              {q
                ? <>Nessuna convenzione corrisponde a <strong>&quot;{q}&quot;</strong>{activeTab !== TUTTI ? <> nella categoria <strong>{activeTab}</strong></> : null}.</>
                : "Nessuna convenzione disponibile."}
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="convenzioni-grid">
              {filtered.map((partner) => (
                <Link
                  key={partner.slug}
                  to={`/convenzione-partner/${partner.slug}`}
                  className="group flex flex-col rounded-xl border border-[var(--confcom-border)] hover:border-[var(--confcom-blue)]/30 hover:shadow-md transition-all overflow-hidden"
                  data-testid={`partner-${partner.slug}`}
                >
                  {/* Logo area */}
                  <div
                    className="flex items-center justify-center h-36 px-6"
                    style={{ backgroundColor: partner.logoBg || "#F5F8FC" }}
                  >
                    {partner.image ? (
                      <img
                        src={partner.image}
                        alt={`Logo ${partner.nome}`}
                        className="max-h-20 max-w-[160px] object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-[var(--confcom-bg-blue-light)] flex items-center justify-center">
                        <Tag size={28} className="text-[var(--confcom-blue)]" />
                      </div>
                    )}
                  </div>

                  {/* Corpo */}
                  <div className="flex flex-col flex-1 p-5">
                    <span className={`self-start text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-2 border ${(CATEGORIE_COLORS[partner.categoria] || { chip: "bg-gray-100 text-gray-600 border-gray-200" }).chip}`}>
                      {partner.categoria}
                    </span>
                    <h2 className="text-base font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-1 group-hover:text-[var(--confcom-blue)] transition-colors leading-snug">
                      {partner.nome}
                    </h2>
                    {partner.tagline && (
                      <p className="text-sm text-[var(--confcom-text-secondary)] line-clamp-2 mt-1">{partner.tagline}</p>
                    )}
                    {partner.scontoLabel && (
                      <span className="mt-auto pt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[var(--confcom-blue)]">
                        <Tag size={13} aria-hidden="true" /> {partner.scontoLabel}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── CONVENZIONI NAZIONALI ── */}
      <section ref={nazionaliRef} id="convenzioni-nazionali" className="py-20 bg-white" data-testid="convenzioni-nazionali">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-4 block">
                Per tutti gli associati
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-tight mb-8">
                Vuoi usufruire delle<br />CONVENZIONI NAZIONALI?
              </h2>
              <p className="text-[var(--confcom-text-secondary)] leading-loose text-base mb-6">
                Confcommercio-Imprese per l'Italia, grazie a particolari convenzioni con importanti aziende del mondo bancario, assicurativo, della telefonia, di automobili e veicoli commerciali, di servizi ICT, dei trasporti, dell'energia, offre ai propri associati la possibilità di usufruire di un insieme di servizi e strumenti a supporto dell'attività d'impresa e delle esigenze degli associati.
              </p>
              <p className="text-[var(--confcom-text-secondary)] leading-loose text-base mb-10">
                Presenta la tua Tessera Associativa ai nostri partner per ottenere sconti, servizi aggiuntivi e particolari condizioni di favore.
              </p>
              <a
                href="https://associati.confcommercio.it/convenzioni-nazionali/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[var(--confcom-blue)] text-white px-7 py-3 rounded-md font-semibold shadow-md hover:opacity-90 transition-opacity text-base"
                data-testid="convenzioni-nazionali-cta"
              >
                Scopri i vantaggi <ArrowRight size={18} />
              </a>
            </div>
            <div className="relative">
              <img
                src="/local-assets/9f3ll2sm_Loghi-nazionale.png"
                alt="Loghi partner Convenzioni Nazionali Confcommercio"
                className="w-full h-[440px] object-contain rounded-2xl bg-white shadow-sm p-4"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── ATTIVA CONVENZIONE CTA ── */}
      <section className="py-14 bg-[var(--confcom-blue)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
                Sei un'azienda partner?
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] mt-2 mb-4">
                Attiva una Convenzione
              </h2>
              <p className="text-white/80 leading-relaxed text-base mb-6">
                Entra nel nostro grande network e fai conoscere la tua attività a tutti gli associati Confcommercio Roma. Contattaci per attivare una convenzione dedicata.
              </p>
              <a
                href="mailto:convenzioni@confcommercioroma.it"
                className="inline-flex items-center gap-3 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-bold hover:opacity-90 transition-opacity text-sm"
                data-testid="convenzioni-email"
              >
                <Mail size={18} /> convenzioni@confcommercioroma.it
              </a>
            </div>
            <div className="bg-white/10 rounded-2xl p-8 border border-white/10">
              <h3 className="text-lg font-bold text-white font-['Encode_Sans_Semi_Condensed'] mb-5">
                Diventa un'azienda partner
              </h3>
              <ul className="space-y-3">
                {[
                  "Visibilità diretta su oltre 30.000 imprese associate a Roma",
                  "Presenza nel portale online Confcommercio Roma",
                  "Comunicazione dedicata ai nostri associati",
                  "Accesso privilegiato a eventi e fiere di settore",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-white/80">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#56B7E6] mt-1.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
}
